import { resolveDateRange } from './date-range'
import type { DateRangeValue } from './date-range'

export const ANALYTICS_TIME_ZONE = 'America/Sao_Paulo'
const DAY = 86_400_000

function dayKey(date: Date) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: ANALYTICS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date)
  const part = (type: string) => parts.find((item) => item.type === type)!.value
  return `${part('year')}-${part('month')}-${part('day')}`
}

function shift(day: string, days: number) {
  return new Date(new Date(`${day}T12:00:00Z`).getTime() + days * DAY).toISOString().slice(0, 10)
}

// Resolve the first instant of a civil day, including historical DST transitions.
function midnight(day: string) {
  const approximate = new Date(`${day}T00:00:00Z`).getTime()
  let low = approximate - DAY
  let high = approximate + DAY
  while (low < high) {
    const middle = Math.floor((low + high) / 2)
    if (dayKey(new Date(middle)) < day) low = middle + 1
    else high = middle
  }
  return new Date(low).toISOString()
}

export function analyticsDateRange(value: DateRangeValue, now = new Date()) {
  const range = resolveDateRange(value, new Date(`${dayKey(now)}T12:00:00Z`))
  return {
    ...(range.dateFrom ? { dateFrom: midnight(range.dateFrom.slice(0, 10)) } : {}),
    ...(range.dateTo ? { dateTo: midnight(range.dateTo.slice(0, 10)) } : {}),
  }
}

export function analyticsPeriod(value: DateRangeValue, now = new Date()) {
  const today = dayKey(now)
  let from = today
  let through = today
  if (value.preset === '7d') from = shift(today, -6)
  if (value.preset === '30d') from = shift(today, -29)
  if (value.preset === 'month') {
    from = `${today.slice(0, 7)}-01`
    through = shift(
      new Date(Date.UTC(Number(today.slice(0, 4)), Number(today.slice(5, 7)), 1))
        .toISOString()
        .slice(0, 10),
      -1,
    )
  }
  if (value.preset === 'custom') {
    const valid = (day: string | null): day is string =>
      Boolean(
        day &&
        /^\d{4}-\d{2}-\d{2}$/.test(day) &&
        !Number.isNaN(Date.parse(`${day}T12:00:00Z`)) &&
        new Date(`${day}T12:00:00Z`).toISOString().slice(0, 10) === day,
      )
    if (!valid(value.from) || !valid(value.to))
      return { error: 'Preencha as duas datas para consultar o período.', query: '', label: '' }
    from = value.from
    through = value.to
    const days = (Date.parse(through) - Date.parse(from)) / DAY + 1
    if (days < 1 || days > 366)
      return { error: 'Escolha um intervalo de 1 a 366 dias.', query: '', label: '' }
  }
  const to = midnight(shift(through, 1))
  const start = value.preset === 'all' ? undefined : midnight(from)
  const query = new URLSearchParams(
    value.preset === 'all' ? { range: 'all', to } : { from: start!, to },
  ).toString()
  const format = (day: string) => day.split('-').reverse().join('/')
  return {
    error: null,
    query,
    from: start,
    to,
    label:
      value.preset === 'all'
        ? `Todo o histórico até ${format(today)}`
        : `${format(from)} a ${format(through)}`,
  }
}

export function analyticsDate(value: string) {
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: ANALYTICS_TIME_ZONE,
    dateStyle: 'short',
  }).format(new Date(value))
}
